import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { INK } from './utils/wizardTheme';
import { getCurrentUser, signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { UserProvider, useCurrentUser, canAccess } from './context/UserContext';
import { getImpersonateId } from './impersonation';
import awsConfig from './aws-config';
import SessionExpiredDialog from './components/SessionExpiredDialog';
import { markSessionExpired, onSessionExpired, rememberReturnPath, takeReturnPath } from './utils/sessionExpiry';
import { PAGE_REGISTRY } from './navRegistry';
import { NavRefreshContext } from './context/NavContext';
import Home from './pages/Home';
import Clients from './pages/Clients';
import Products from './pages/Products';
import Programs from './pages/Programs';
import ProgramWizard from './pages/ProgramWizard';
import Components from './pages/Components';
import Goals from './pages/Goals';
import LeadUpload from './pages/LeadUpload';
import SupplierPortal from './pages/SupplierPortal';
import MyHome from './pages/MyHome';
import WeekAtAGlance from './pages/WeekAtAGlance';
import Invoices from './pages/Invoices';
import TargetingLists from './pages/TargetingLists';
import Suppliers from './pages/Suppliers';
import ReportBillableSupplier from './pages/ReportBillableSupplier';
import ReportBillableClient from './pages/ReportBillableClient';
import ReportRevenue from './pages/ReportRevenue';
import Admin from './pages/Admin';
import EmailAlerts from './pages/EmailAlerts';
import ComponentLibrary from './pages/ComponentLibrary';
import LeadQA from './pages/LeadQA';
import QARules from './pages/QARules';
import LeadDelivery from './pages/LeadDelivery';
import LeadDeliverySetup from './pages/LeadDeliverySetup';
import ClientDeliveryWizard from './pages/ClientDeliveryWizard';
import NavAdmin from './pages/NavAdmin';
import RecycleBin from './pages/RecycleBin';
import SystemHealth from './pages/SystemHealth';
import GlobalOptOuts from './pages/GlobalOptOuts';
import ProgramDocuments from './pages/ProgramDocuments';
import ProgramDocumentEditor from './pages/ProgramDocumentEditor';
import BacklogQueue from './pages/BacklogQueue';
import ContentIQ from './pages/contentiq/ContentIQ';
import ContentIQAdmin from './pages/contentiq/ContentIQAdmin';
import ContentIQAssets from './pages/contentiq/ContentIQAssets';
import ContentIQBuilds from './pages/contentiq/ContentIQBuilds';
import ContentIQDeckStart from './pages/contentiq/ContentIQDeckStart';
import ContentIQHome from './pages/contentiq/ContentIQHome';
import ContentIQSetupWizard from './pages/contentiq/ContentIQSetupWizard';
import ContentIQStart from './pages/contentiq/ContentIQStart';

const API = process.env.REACT_APP_API_URL;
const SHOW_RESET = process.env.REACT_APP_SHOW_RESET === 'true';

// ── Axios auth interceptor ────────────────────────────────────────────────────
axios.interceptors.request.use(async (config) => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // not authenticated
  }
  // Attach impersonation header if active
  const impId = getImpersonateId();
  if (impId) config.headers['X-Impersonate'] = impId;
  return config;
});

// A 401 from our API only ever means the login is gone (backend app/auth.py),
// so it raises the "You've been signed out" dialog instead of letting the page
// render empty data as if it were real. Other hosts' 401s are left alone.
axios.interceptors.response.use(undefined, (error) => {
  if (error?.response?.status === 401 && API && String(error.config?.url || '').startsWith(API)) {
    markSessionExpired();
  }
  return Promise.reject(error);
});

// "Sign in again" from the dialog. Amplify refuses a new redirect while it still
// holds the stale tokens, so drop those locally (no Cognito logout round trip)
// and start the redirect; the saved path brings the person back afterwards.
async function signInAgain() {
  rememberReturnPath(window.location.pathname + window.location.search + window.location.hash);
  try {
    await signInWithRedirect();
  } catch (e) {
    const clientId = awsConfig.Auth.Cognito.userPoolClientId;
    try {
      Object.keys(window.localStorage)
        .filter(k => k.startsWith(`CognitoIdentityServiceProvider.${clientId}.`))
        .forEach(k => window.localStorage.removeItem(k));
    } catch (err) { /* storage blocked */ }
    await signInWithRedirect();
  }
}

// ── Role-guarded route ────────────────────────────────────────────────────────
function GuardedRoute({ page, element }) {
  const { currentUser, userLoading, connection, reloadUser } = useCurrentUser();
  if (connection === 'reconnecting') return <div role="status" style={{ padding: '40px', color: INK.secondary }}>Reconnecting to DemandArc…</div>;
  if (userLoading) return null;
  if (connection === 'unreachable') {
    return (
      <div role="alert" style={{ padding: '40px', color: INK.secondary }}>
        DemandArc isn’t answering right now — it may be restarting.{' '}
        <button onClick={reloadUser} style={{ marginLeft: '6px' }}>Try again</button>
      </div>
    );
  }
  if (!currentUser) return <div style={{ padding: '40px', color: '#e74c3c' }}>Access denied — contact an admin to set up your account.</div>;
  if (!canAccess(currentUser.role, page)) return <Navigate to="/" replace />;
  return element;
}

// ── Nav link with active styling ──────────────────────────────────────────────
function NavLink({ to, children }) {
  const loc = useLocation();
  const active = loc.pathname === to;
  return (
    <Link to={to} style={{
      color: active ? 'white' : '#bdc3c7',
      textDecoration: 'none',
      fontWeight: active ? '600' : 'normal',
      borderBottom: active ? '2px solid #3498db' : '2px solid transparent',
      paddingBottom: '2px',
    }}>
      {children}
    </Link>
  );
}

// ── Nav dropdown group ────────────────────────────────────────────────────────
function NavDropdown({ label, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  const isActive = items.some(item => pathname === item.to);

  // Close when clicking anywhere outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 2px 0',
          color: isActive ? 'white' : '#bdc3c7',
          fontWeight: isActive ? '600' : 'normal',
          borderBottom: isActive ? '2px solid #3498db' : '2px solid transparent',
          fontSize: '14px', fontFamily: 'Arial, sans-serif',
          display: 'flex', alignItems: 'center', gap: '4px',
        }}
      >
        {label}
        <span style={{ fontSize: '9px', opacity: 0.7, marginTop: '1px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 10px)', left: 0,
          background: '#2c3e50', border: '1px solid #34495e',
          borderRadius: '6px', minWidth: '170px', zIndex: 1000,
          boxShadow: '0 6px 20px rgba(0,0,0,0.4)', overflow: 'hidden',
        }}>
          {items.map(item => {
            const itemActive = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                style={{
                  display: 'block', padding: '10px 16px', textDecoration: 'none',
                  fontSize: '13px',
                  color: itemActive ? 'white' : '#bdc3c7',
                  fontWeight: itemActive ? '600' : 'normal',
                  background: itemActive ? 'rgba(52,152,219,0.3)' : 'transparent',
                }}
                onMouseEnter={e => { if (!itemActive) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = itemActive ? 'rgba(52,152,219,0.3)' : 'transparent'; }}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Render a dropdown if multiple items, a plain NavLink if only one, nothing if empty
function NavGroup({ label, items }) {
  if (items.length === 0) return null;
  if (items.length === 1) return <NavLink to={items[0].to}>{items[0].label}</NavLink>;
  return <NavDropdown label={label} items={items} />;
}

// ── Title bar updater ─────────────────────────────────────────────────────────
const IS_STAGING = (process.env.REACT_APP_API_URL || '').includes('staging');

const PAGE_TITLES = {
  '/':                 'DemandArc',
  '/programs':         'Programs | DemandArc',
  '/programs/new':     'New Program | DemandArc',
  '/program-documents': 'Program Documents | DemandArc',
  '/components':       'Components | DemandArc',
  '/goals':            'Goals | DemandArc',
  '/lead-upload':      'Lead Status Updater | DemandArc',
  '/lead-qa':          'Lead QA Review | DemandArc',
  '/qa-rules':         'Campaign QA Rules | DemandArc',
  '/lead-delivery':    'Client Leads Delivery | DemandArc',
  '/lead-delivery-setup': 'Delivery Setup | DemandArc',
  '/supplier-portal':  'Supplier Portal | DemandArc',
  '/pm-home':          'My Home | DemandArc',
  '/week-at-a-glance': 'Week at a Glance | DemandArc',
  '/invoices':         'Invoices | DemandArc',
  '/reports/billable-by-supplier': 'Billable Leads by Supplier | DemandArc',
  '/reports/billable-by-client':   'Billable Leads by Client | DemandArc',
  '/reports/revenue':              'Revenue Recognition | DemandArc',
  '/targeting-lists':  'List Management | DemandArc',
  '/suppliers':        'Suppliers | DemandArc',
  '/clients':          'Clients | DemandArc',
  '/admin':            'Admin | DemandArc',
  '/admin/navigation': 'Navigation | DemandArc',
  '/admin/recycle-bin': 'Recycle Bin | DemandArc',
  '/admin/system-health': 'System Health | DemandArc',
  '/admin/opt-outs': 'DNE / DNC from Emails | DemandArc',
  '/admin/backlog-queue': 'Backlog Queue | DemandArc',
};

function TitleUpdater() {
  const { pathname } = useLocation();
  useEffect(() => {
    const base = PAGE_TITLES[pathname] || 'DemandArc';
    document.title = IS_STAGING ? `[Staging] ${base}` : base;
  }, [pathname]);
  return null;
}

// Moving to a different page starts at the top. The router doesn't do this on
// its own, so arriving from halfway down a long page used to land halfway down
// the next one. Keyed on the path only: a query-string change (e.g.
// ?program_id=) is the same page and keeps its scroll. Pages that jump to a
// section (My Home's #pending-deliveries) do it after their own data loads,
// which runs after this.
function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Keeps the root's scroll-padding equal to the pinned header's height, so
// scrollIntoView() and anchor jumps land below the nav bar, not under it. The
// height changes when the impersonation banner appears or the nav wraps.
function usePinnedHeaderOffset(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const root = document.documentElement;
    const apply = () => { root.style.scrollPaddingTop = `${el.offsetHeight}px`; };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.scrollPaddingTop = ''; };
  }, [ref]);
}

// ── Impersonation banner ──────────────────────────────────────────────────────
function ImpersonationBanner() {
  const { currentUser, impersonatingId, stopImpersonating } = useCurrentUser();
  if (!impersonatingId) return null;
  return (
    <div style={{
      background: '#e67e22',
      color: 'white',
      padding: '8px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      fontSize: '13px',
      fontWeight: '500',
    }}>
      <span>⚠️ Viewing as <strong>{currentUser?.name}</strong> ({currentUser?.role?.replace(/_/g, ' ')})</span>
      <button
        onClick={stopImpersonating}
        style={{
          padding: '4px 12px',
          background: 'white',
          color: '#e67e22',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: '600',
          fontSize: '12px',
        }}
      >
        Stop Impersonating
      </button>
    </div>
  );
}

// ── Main app shell ────────────────────────────────────────────────────────────
function AppShell({ user, onSignOut }) {
  const { currentUser, impersonatingId } = useCurrentUser();

  // Nav is backend-driven: GET /nav returns the role-resolved tree (the backend
  // filters by the current/impersonated user's role and hides admin-only items
  // while impersonating). Re-fetch when the effective user changes.
  // `refreshNav` is also handed to pages through NavRefreshContext — the
  // Navigation editor calls it after each change so the bar updates in place.
  const [navNodes, setNavNodes] = useState([]);
  const navRequest = useRef(0);
  const refreshNav = useCallback(() => {
    if (!currentUser) { setNavNodes([]); return Promise.resolve(); }
    const req = ++navRequest.current;   // a newer request wins over a slower older one
    return axios.get(`${API}/nav/`)
      .then(res => { if (req === navRequest.current) setNavNodes(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (req === navRequest.current) setNavNodes([]); });
  }, [currentUser]);
  useEffect(() => { refreshNav(); }, [refreshNav, impersonatingId]);

  // The nav bar and impersonation banner stay pinned to the top of the window
  // on every page. zIndex 50 sits above page content but below every modal
  // overlay in the app (60 and up), so a modal still covers the nav.
  const headerRef = useRef(null);
  usePinnedHeaderOffset(headerRef);

  return (
    <NavRefreshContext.Provider value={refreshNav}>
    <Router>
      <div style={{ fontFamily: 'Arial, sans-serif' }}>
        <TitleUpdater />
        <ScrollToTopOnNavigate />
        <div ref={headerRef} style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <nav style={{ background: '#2c3e50', padding: '12px 24px', display: 'flex', gap: '24px', alignItems: 'center' }}>
          <NavLink to="/">DemandArc</NavLink>

          {/* Backend-driven nav (see navRegistry.js + /nav). page_key → path via
              the registry; label + grouping + order + role visibility come from
              the DB nav config. NavGroup collapses to a link/null as before. */}
          {navNodes.map((node, idx) => {
            if (node.type === 'item') {
              const reg = PAGE_REGISTRY[node.page_key];
              if (!reg) return null;
              return <NavLink key={`item-${node.page_key}`} to={reg.path}>{node.label}</NavLink>;
            }
            // group
            const items = node.items
              .map(i => {
                const reg = PAGE_REGISTRY[i.page_key];
                return reg ? { to: reg.path, label: i.label } : null;
              })
              .filter(Boolean);
            return <NavGroup key={`group-${node.label}-${idx}`} label={node.label} items={items} />;
          })}

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
            {currentUser && (
              <span style={{ color: impersonatingId ? '#e67e22' : '#7f8c8d', fontSize: '13px' }}>
                {currentUser.name}
                <span style={{
                  marginLeft: '6px',
                  background: impersonatingId ? '#e67e22' : '#34495e',
                  borderRadius: '4px',
                  padding: '1px 6px',
                  fontSize: '11px',
                  color: 'white',
                }}>
                  {currentUser.role.replace(/_/g, ' ')}
                </span>
              </span>
            )}
            {!impersonatingId && (
              <span style={{ color: '#bdc3c7', fontSize: '13px' }}>{user.signInDetails?.loginId}</span>
            )}
            {SHOW_RESET && !impersonatingId && (
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure? This will delete ALL data.')) {
                    await axios.post(`${API}/admin/reset-db`);
                    window.location.reload();
                  }
                }}
                style={{ padding: '6px 16px', background: '#c0392b', color: 'white', border: '2px solid #e74c3c', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Reset Database
              </button>
            )}
            {!impersonatingId && (
              <button onClick={onSignOut} style={{ padding: '6px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Sign Out
              </button>
            )}
          </div>
        </nav>

        <ImpersonationBanner />
        </div>

        <div style={{ padding: '24px' }}>
          <Routes>
            <Route path="/"                      element={currentUser?.role === 'partner' ? <Navigate to="/supplier-portal" replace /> : <Home />} />
            <Route path="/clients"             element={<GuardedRoute page="clients"             element={<Clients />} />} />
            <Route path="/products"            element={<GuardedRoute page="products"            element={<Products />} />} />
            <Route path="/programs"            element={<GuardedRoute page="programs"            element={<Programs />} />} />
            {/* Before /programs/:anything-style routes would shadow it — this is a
                literal path, so order is not load-bearing, but keep it adjacent. */}
            <Route path="/programs/new"        element={<GuardedRoute page="program-new"         element={<ProgramWizard />} />} />
            <Route path="/components"          element={<GuardedRoute page="components"          element={<Components />} />} />
            <Route path="/goals"               element={<GuardedRoute page="goals"               element={<Goals />} />} />
            <Route path="/lead-upload"         element={<GuardedRoute page="lead-upload"         element={<LeadUpload />} />} />
            <Route path="/lead-qa"             element={<GuardedRoute page="lead-qa"             element={<LeadQA />} />} />
            <Route path="/qa-rules"            element={<GuardedRoute page="qa-rules"            element={<QARules />} />} />
            <Route path="/lead-delivery"       element={<GuardedRoute page="lead-delivery"       element={<LeadDelivery />} />} />
            <Route path="/lead-delivery-setup" element={<GuardedRoute page="lead-delivery-setup" element={<LeadDeliverySetup />} />} />
            <Route path="/client-delivery-wizard" element={<GuardedRoute page="client-delivery-wizard" element={<ClientDeliveryWizard />} />} />
            <Route path="/supplier-portal"     element={<GuardedRoute page="supplier-portal"     element={<SupplierPortal />} />} />
            <Route path="/pm-home"             element={<GuardedRoute page="pm-home"             element={<MyHome />} />} />
            <Route path="/week-at-a-glance"    element={<GuardedRoute page="week-glance"         element={<WeekAtAGlance />} />} />
            <Route path="/invoices"            element={<GuardedRoute page="invoices"            element={<Invoices />} />} />
            <Route path="/targeting-lists"     element={<GuardedRoute page="targeting-lists"     element={<TargetingLists />} />} />
            <Route path="/suppliers"           element={<GuardedRoute page="suppliers"           element={<Suppliers />} />} />
            <Route path="/reports/billable-by-supplier" element={<GuardedRoute page="reports-billable-supplier" element={<ReportBillableSupplier />} />} />
            <Route path="/reports/billable-by-client"   element={<GuardedRoute page="reports-billable-client"   element={<ReportBillableClient />} />} />
            <Route path="/reports/revenue"               element={<GuardedRoute page="reports-revenue"           element={<ReportRevenue />} />} />
            <Route path="/admin"               element={<GuardedRoute page="admin"               element={<Admin />} />} />
            <Route path="/admin/email-alerts" element={<GuardedRoute page="email-alerts"        element={<EmailAlerts />} />} />
            <Route path="/admin/component-library" element={<GuardedRoute page="component-library" element={<ComponentLibrary />} />} />
            <Route path="/admin/navigation"        element={<GuardedRoute page="navigation"         element={<NavAdmin />} />} />
            <Route path="/admin/recycle-bin"       element={<GuardedRoute page="recycle-bin"        element={<RecycleBin />} />} />
            <Route path="/admin/system-health"     element={<GuardedRoute page="system-health"      element={<SystemHealth />} />} />
            <Route path="/program-documents"       element={<GuardedRoute page="program-documents"  element={<ProgramDocuments />} />} />
            <Route path="/program-documents/:id"   element={<GuardedRoute page="program-documents"  element={<ProgramDocumentEditor />} />} />
            <Route path="/admin/opt-outs"          element={<GuardedRoute page="global-opt-outs"    element={<GlobalOptOuts />} />} />
            <Route path="/admin/backlog-queue"     element={<GuardedRoute page="backlog-queue"      element={<BacklogQueue />} />} />
            {/* ContentIQ — admin-only pilot: nav entries seeded by backend migration 0079 (admin role only) */}
            <Route path="/contentiq"               element={<GuardedRoute page="contentiq"          element={<ContentIQHome />} />} />
            <Route path="/contentiq/advanced"      element={<GuardedRoute page="contentiq"          element={<ContentIQ />} />} />
            <Route path="/contentiq/new"           element={<GuardedRoute page="contentiq"          element={<ContentIQSetupWizard />} />} />
            <Route path="/contentiq/start/:output" element={<GuardedRoute page="contentiq"          element={<ContentIQStart />} />} />
            <Route path="/contentiq/deck"          element={<GuardedRoute page="contentiq"          element={<ContentIQDeckStart />} />} />
            <Route path="/contentiq/project/:projectId" element={<GuardedRoute page="contentiq"     element={<ContentIQSetupWizard />} />} />
            {/* Old stage-wizard links land on the unified project screen. */}
            <Route path="/contentiq/wizard/:projectId" element={<GuardedRoute page="contentiq"      element={<ContentIQSetupWizard />} />} />
            <Route path="/contentiq/admin"         element={<GuardedRoute page="contentiq-admin"    element={<ContentIQAdmin />} />} />
            <Route path="/contentiq/assets"        element={<GuardedRoute page="contentiq-assets"   element={<ContentIQAssets />} />} />
            <Route path="/contentiq/builds"        element={<GuardedRoute page="contentiq-builds"   element={<ContentIQBuilds />} />} />
            <Route path="/contentiq/builds/:packageId" element={<GuardedRoute page="contentiq-builds" element={<ContentIQBuilds />} />} />
            <Route path="/contentiq/packages"      element={<Navigate to="/contentiq/advanced" replace />} />
          </Routes>
        </div>
      </div>
    </Router>
    </NavRefreshContext.Provider>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => { checkUser(); }, []);
  useEffect(() => onSessionExpired(() => setSessionExpired(true)), []);

  // Coming back to a tab left open for hours: check the login before anyone
  // reads a stale "0" as real, rather than waiting for the next request.
  useEffect(() => {
    if (!user) return undefined;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) markSessionExpired();
      } catch { markSessionExpired(); }
    };
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, [user]);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      // Back from a "Sign in again": put the person on the page they were on.
      // Done before the router mounts, so it simply starts there.
      const returnTo = takeReturnPath();
      if (returnTo && window.location.pathname === '/') window.history.replaceState(null, '', returnTo);
      setUser(currentUser);
    } catch {
      setUser(null);
    }
    setLoading(false);
  };

  const handleSignIn = () => signInWithRedirect();
  const handleSignOut = async () => { await signOut(); setUser(null); };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;

  if (!user) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ color: '#2c3e50', marginBottom: '8px' }}>DemandArc Order Entry</h1>
      <p style={{ color: '#7f8c8d', marginBottom: '32px' }}>Sign in to continue</p>
      <button onClick={handleSignIn} style={{ padding: '12px 32px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer' }}>
        Sign In
      </button>
    </div>
  );

  return (
    <UserProvider>
      <AppShell user={user} onSignOut={handleSignOut} />
      {sessionExpired && <SessionExpiredDialog onSignIn={signInAgain} />}
    </UserProvider>
  );
}

export default App;
